import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardBody, CardHeader, Container, Row, Col } from "reactstrap";
import Header from "components/Headers/Header.js";
import { apiUrl } from "../../constant";
import { exportAuditDrilldownExcel } from "./auditDrilldownExport";
import AuditFilterBar from "./components/AuditFilterBar";
import AuditSummaryCards from "./components/AuditSummaryCards";
import ManagerStackBarChart from "./components/ManagerStackBarChart";
import PhaseStackBarChart from "./components/PhaseStackBarChart";
import AuditDrillDownModal from "./components/AuditDrillDownModal";

const LEGEND_ITEMS = [
  { label: "Not Started",            color: "#0060B8" },
  { label: "Ready to Start",         color: "#2dce89" },
  { label: "In Progress",            color: "#FEA732" },
  { label: "On Hold",                color: "#FF6430" },
  { label: "TB Approval",            color: "rgba(186, 156, 255, 0.4)" },
  { label: "Basic Analysis Started", color: "#E6399B" },
  { label: "Checklist Done",         color: "#5e60ce" },
];

const Legend = () => (
  <div className="d-flex flex-wrap align-items-center" style={{ fontSize: 12, gap: "1rem" }}>
    {LEGEND_ITEMS.map(({ label, color }) => (
      <span key={label}>
        <span style={{
          display: "inline-block", width: 12, height: 12, background: color,
          marginRight: 4, verticalAlign: "middle", border: "1px solid rgba(0,0,0,0.1)",
        }} />
        {label}
      </span>
    ))}
    <span>
      <span style={{
        display: "inline-block", width: 14, height: 12, marginRight: 4, verticalAlign: "middle",
        backgroundImage: "repeating-linear-gradient(45deg, #888 0 2px, #ddd 2px 6px)",
        border: "1px solid rgba(0,0,0,0.1)",
      }} />
      Due in 2 Days (striped)
    </span>
    <span>
      <span style={{
        display: "inline-block", width: 14, height: 12, marginRight: 4, verticalAlign: "middle",
        backgroundImage: "repeating-linear-gradient(45deg, #888 0 2px, #d62728 2px 5px)",
        border: "1px solid rgba(0,0,0,0.1)",
      }} />
      Overdue (red striped)
    </span>
  </div>
);

const AuditWorkflowDashboard = () => {
  const userInfo = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user-info")) || {}; }
    catch { return {}; }
  }, []);
  const roleId = Number(userInfo?.role_id);
  const isManager = roleId === 12 || roleId === 14;
  const isSeniorManager = roleId === 14;
  const isAnalystOnly = roleId === 10; // role 10 = analyst: phase chart only, no tasks chart, minimal filters

  const authHeader = useMemo(() => {
    const token = localStorage.getItem("authtoken");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const [filters, setFilters] = useState({
    role: null,
    client_types: [],
    job_types: [],
    selected_user: null,
  });

  const [activeTab, setActiveTab] = useState(null);
  const [chartTab, setChartTab] = useState(isAnalystOnly ? "phase" : "manager"); // 'manager' | 'phase'
  const [managerChart, setManagerChart] = useState([]);
  const [phaseChart, setPhaseChart] = useState([]);
  const [summary, setSummary]           = useState({ in_progress: 0, on_hold: 0, ready_to_start: 0, tb_approved: 0, basic_analysis_started: 0, checklist_done: 0, due_soon: 0, overdue: 0 });
  const [phaseSummary, setPhaseSummary] = useState({ in_progress: 0, on_hold: 0, ready_to_start: 0, tb_approved: 0, basic_analysis_started: 0, checklist_done: 0, due_soon: 0, overdue: 0 });
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [drillDown, setDrillDown] = useState({ open: false, params: null });

  const queryParams = useMemo(() => buildQuery(filters), [filters]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${apiUrl}/audit-dashboard/chart/phase`,   { headers: authHeader, params: queryParams }),
        axios.get(`${apiUrl}/audit-dashboard/summary/phase`, { headers: authHeader, params: queryParams }),
      ];
      if (!isAnalystOnly) {
        requests.push(axios.get(`${apiUrl}/audit-dashboard/chart/manager`, { headers: authHeader, params: queryParams }));
        requests.push(axios.get(`${apiUrl}/audit-dashboard/summary`,        { headers: authHeader, params: queryParams }));
      }

      const responses = await Promise.all(requests);
      setPhaseChart(responses[0].data?.data ?? []);
      setPhaseSummary(responses[1].data ?? { in_progress: 0, on_hold: 0, ready_to_start: 0, tb_approved: 0, basic_analysis_started: 0, checklist_done: 0, due_soon: 0, overdue: 0 });
      if (!isAnalystOnly) {
        setManagerChart(responses[2].data?.data ?? []);
        setSummary(responses[3].data ?? { in_progress: 0, on_hold: 0, ready_to_start: 0, tb_approved: 0, basic_analysis_started: 0, checklist_done: 0, due_soon: 0, overdue: 0 });
      } else {
        setManagerChart([]);
        setSummary({ in_progress: 0, on_hold: 0, ready_to_start: 0, tb_approved: 0, basic_analysis_started: 0, checklist_done: 0, due_soon: 0, overdue: 0 });
      }
    } catch (err) {
      setManagerChart([]);
      setPhaseChart([]);
      setSummary({ in_progress: 0, on_hold: 0, ready_to_start: 0, tb_approved: 0, basic_analysis_started: 0, checklist_done: 0, due_soon: 0, overdue: 0 });
      setPhaseSummary({ in_progress: 0, on_hold: 0, ready_to_start: 0, tb_approved: 0, basic_analysis_started: 0, checklist_done: 0, due_soon: 0, overdue: 0 });
    } finally {
      setLoading(false);
    }
  }, [authHeader, queryParams, isAnalystOnly]);

  useEffect(() => {
    if (!isManager) return;
    axios
      .get(`${apiUrl}/audit-dashboard/assigned-users`, { headers: authHeader })
      .then((res) => setAssignedUsers(res.data?.data ?? []))
      .catch(() => setAssignedUsers([]));
  }, [authHeader, isManager]);

  useEffect(() => {
    const t = setTimeout(fetchAll, 300);
    return () => clearTimeout(t);
  }, [fetchAll]);

  const handleTabClick = (bucket) => {
    setActiveTab(bucket);
    // Marker tells the modal (and the backend) to use the phase-mode drilldown
    // when the user is on the phase tab — even without a specific phase_id.
    const params = (chartTab === "phase" || isAnalystOnly) ? { bucket, phase_mode: 1 } : { bucket };
    setDrillDown({ open: true, params });
  };

  const handleSegmentClick = (params) => {
    setDrillDown({ open: true, params });
  };

  const filteredManagerChart = useMemo(() => {
    if (!activeTab || activeTab === "due_soon" || activeTab === "overdue") return managerChart;
    return managerChart.map((row) => applyTabFilter(row, activeTab));
  }, [managerChart, activeTab]);

  const filteredPhaseChart = useMemo(() => {
    if (!activeTab || activeTab === "due_soon" || activeTab === "overdue") return phaseChart;
    return phaseChart.map((row) => applyTabFilter(row, activeTab));
  }, [phaseChart, activeTab]);

  const clearTab = () => {
    setActiveTab(null);
    setDrillDown({ open: false, params: null });
  };

  const onPhaseTab = chartTab === "phase" || isAnalystOnly;
  const reviewerMode = Number(filters.role?.value) === 4;

  // Status-filter dropdown options mirror the visible cards, so the dropdown and the
  // cards stay in sync (both drive `activeTab`).
  const statusFilterOptions = useMemo(() => {
    const buckets = onPhaseTab || reviewerMode ? PHASE_FILTER_BUCKETS : MANAGER_FILTER_BUCKETS;
    return buckets.map((b) => ({ value: b, label: labelForBucket(b) }));
  }, [onPhaseTab, reviewerMode]);

  const statusValue = useMemo(
    () => statusFilterOptions.find((o) => o.value === activeTab) || null,
    [statusFilterOptions, activeTab]
  );

  // Selecting a status in the dropdown filters the dashboard (charts + cards + export)
  // WITHOUT opening the drill-down modal; clicking a card still opens it via handleTabClick.
  const setStatusFilter = (option) => setActiveTab(option?.value ?? null);

  // Header-level Excel export of the drill-down detail rows (same data the popup shows).
  // If a status card is active, export just that bucket; otherwise export every status
  // for the current tab. Dropdown filters are always applied.
  const handleExportExcel = async () => {
    if (exporting) return;

    const baseParams = { ...queryParams };
    if (onPhaseTab) baseParams.phase_mode = 1;

    let buckets;
    let label;
    if (activeTab) {
      buckets = [activeTab];
      label = `${onPhaseTab ? "phase" : "manager"}-${activeTab}`;
    } else {
      buckets = onPhaseTab || reviewerMode ? PHASE_STATUS_BUCKETS : MANAGER_STATUS_BUCKETS;
      label = `${onPhaseTab ? "phase" : "manager"}-all`;
    }

    setExporting(true);
    try {
      const count = await exportAuditDrilldownExcel({
        authHeader,
        baseParams,
        buckets,
        isPhaseMode: onPhaseTab,
        filenameLabel: label,
      });
      if (count === 0) window.alert("No rows to export for the current selection.");
    } catch (err) {
      window.alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0">Audit &amp; Support Workflow Dashboard</h3>
                  {activeTab && (
                    <small className="text-muted">
                      Filtered by: <strong>{labelForBucket(activeTab)}</strong>
                      <button type="button" className="btn btn-link btn-sm py-0" onClick={clearTab}>clear</button>
                    </small>
                  )}
                </div>
                <div className="d-flex align-items-center" style={{ gap: "1rem" }}>
                  <Legend />
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={handleExportExcel}
                    disabled={loading || exporting}
                    title={activeTab ? `Export ${labelForBucket(activeTab)} rows` : "Export all statuses"}
                  >
                    <i className={`fas ${exporting ? "fa-spinner fa-spin" : "fa-download"} mr-1`} />
                    {exporting ? "Exporting…" : "Export to Excel"}
                  </button>
                </div>
              </CardHeader>
              <CardBody>
                <AuditFilterBar
                  filters={filters}
                  onChange={setFilters}
                  onRefresh={fetchAll}
                  showUserPicker={isManager}
                  assignedUsers={assignedUsers}
                  loading={loading}
                  minimal={isAnalystOnly}
                  statusOptions={statusFilterOptions}
                  statusValue={statusValue}
                  onStatusChange={setStatusFilter}
                />

                {!isAnalystOnly && (
                  <div className="d-flex mb-3" style={{ borderBottom: "1px solid #e9ecef" }}>
                    <button
                      type="button"
                      onClick={() => { setChartTab("manager"); setActiveTab(null); }}
                      style={{
                        cursor: "pointer",
                        border: "none",
                        background: "transparent",
                        padding: "10px 18px",
                        color: chartTab === "manager" ? "#5e72e4" : "#525f7f",
                        fontWeight: chartTab === "manager" ? 600 : 500,
                        borderBottom: chartTab === "manager" ? "2px solid #5e72e4" : "2px solid transparent",
                        marginBottom: "-1px",
                      }}
                    >
                      Manager-wise Workload
                    </button>
                    <button
                      type="button"
                      onClick={() => { setChartTab("phase"); setActiveTab(null); }}
                      style={{
                        cursor: "pointer",
                        border: "none",
                        background: "transparent",
                        padding: "10px 18px",
                        color: chartTab === "phase" ? "#5e72e4" : "#525f7f",
                        fontWeight: chartTab === "phase" ? 600 : 500,
                        borderBottom: chartTab === "phase" ? "2px solid #5e72e4" : "2px solid transparent",
                        marginBottom: "-1px",
                      }}
                    >
                      Phase-wise Distribution
                    </button>
                  </div>
                )}

                {chartTab === "manager" && !isAnalystOnly ? (
                  <>
                    <AuditSummaryCards
                      counts={summary}
                      loading={loading}
                      activeBucket={activeTab}
                      onTabClick={handleTabClick}
                      reviewerMode={Number(filters.role?.value) === 4}
                    />
                    <ManagerStackBarChart
                      data={filteredManagerChart}
                      loading={loading}
                      onSegmentClick={handleSegmentClick}
                    />
                  </>
                ) : (
                  <>
                    <AuditSummaryCards
                      counts={phaseSummary}
                      loading={loading}
                      activeBucket={activeTab}
                      onTabClick={handleTabClick}
                      reviewerMode={true}
                    />
                    <PhaseStackBarChart
                      data={filteredPhaseChart}
                      loading={loading}
                      onSegmentClick={handleSegmentClick}
                    />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <AuditDrillDownModal
        isOpen={drillDown.open}
        params={drillDown.params}
        baseFilters={filters}
        onClose={() => setDrillDown({ open: false, params: null })}
      />
    </>
  );
};

const BUCKET_LABELS = {
  in_progress:            "In Progress",
  ready_to_start:         "Ready to Start",
  not_started:            "Not Started",
  on_hold:                "On Hold",
  tb_approved:            "TB Approval",
  overdue:                "Overdue",
  basic_analysis_started: "Basic Analysis Started",
  checklist_done:         "Checklist Done",
  due_soon:               "Due in 2 Days",
};
const labelForBucket = (b) => BUCKET_LABELS[b] || b;

const CHART_BUCKET_KEYS = [
  "in_progress",
  "not_started",
  "ready_to_start",
  "on_hold",
  "tb_approved",
  "basic_analysis_started",
  "checklist_done",
];

function applyTabFilter(row, activeTab) {
  const kept = {};
  CHART_BUCKET_KEYS.forEach((k) => {
    kept[k] = k === activeTab ? row[k] : 0;
    kept[`${k}_due_soon`] = k === activeTab ? row[`${k}_due_soon`] : 0;
  });
  return { ...row, ...kept };
}

// Status buckets to export when NO card is selected ("all statuses"). These are the
// mutually-exclusive status cards only — due_soon / overdue are time-overlays on those
// statuses, so including them would duplicate rows.
const MANAGER_STATUS_BUCKETS = [
  "not_started",
  "ready_to_start",
  "in_progress",
  "on_hold",
  "tb_approved",
  "basic_analysis_started",
  "checklist_done",
];
const PHASE_STATUS_BUCKETS = ["not_started", "in_progress", "on_hold"];

// Status-filter dropdown options mirror the cards, so they include the due_soon / overdue
// overlays too (a card click can set activeTab to either).
const MANAGER_FILTER_BUCKETS = [...MANAGER_STATUS_BUCKETS, "due_soon", "overdue"];
const PHASE_FILTER_BUCKETS = [...PHASE_STATUS_BUCKETS, "due_soon", "overdue"];

function buildQuery(filters) {
  if (!filters) return {};
  const out = {};
  if (filters.role?.value != null)  out.role              = filters.role.value;
  if (filters.client_types?.length) out.client_types      = filters.client_types.map((c) => c.value).join(",");
  if (filters.job_types?.length)    out.job_types         = filters.job_types.map((j) => j.value).join(",");
  if (filters.selected_user?.value) out.selected_user_id  = filters.selected_user.value;
  return out;
}

export default AuditWorkflowDashboard;
